import {
  deserify,
  getMediaBasePath,
  getTeacherSubjectEnum,
} from '@geneo2-web/shared-ui';
import { Subject_SubjectEnum } from '@protos/common/common.db_pb';
import {
  MultiSubjectFormateInfo,
  SubSectionQuestionType,
  SubSectionType,
  SubSectionTypeV2,
  TaskInfoModel,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  ChapterIdsInfo,
  MultiSubjectSection,
  MultiSubjectSectionTemplate,
  MultiSubjectSubSection,
  QpgSubsectionTypeEnum,
  QuestionSectionMeta,
  SectionQuestionMetaData,
  SubSectionQuestionTemplate,
  SubSectionTemplate,
  TaskInfo,
  TopicIdsInfo,
} from '@protos/learning_management/lms.db_pb';
import { TeachClassSubjects } from '@protos/user_management/ums.login.apis_pb';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  setQPGAssessmentContent,
  setQPGQuestionModuleMap,
  setQPGQuestionsSequenceInfo,
} from '../reducer/assessmentFlow.slice';
import {
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import { QuestionTypeCount } from '@protos/learning_management/lms.hw.teacher.apis_pb';
import { getQuestionTypeTitleAndEnum } from '../Common/ReviewAssessment/functions';

// =============================================================

/**
 * Builds a MultiSubjectSectionTemplate from the current section and subsection data
 * for use in createQuestionPaperTemplate (saveSectionTemplates).
 */
export const createMultiSubjectSectionTemplate = (props: {
  multiSubjectSection: MultiSubjectSection | undefined;
  subsectionData: SubSectionType[];
}): MultiSubjectSectionTemplate => {
  const { multiSubjectSection, subsectionData } = props;
  const sectionTittle =
    multiSubjectSection?.sectionTitle || multiSubjectSection?.sectionName || '';

  let noOfQuestions = 0;
  let marks = 0;

  const subSectionTemplate: SubSectionTemplate[] = (subsectionData ?? []).map(
    (sub) => {
      const subSectionQuestionTemplate: SubSectionQuestionTemplate[] = (
        sub.subSectionQuestions ?? []
      ).map(
        (q: SubSectionQuestionType) =>
          new SubSectionQuestionTemplate({
            questionType: q.questionType,
            noOfQuestion: q.noOfQuestion ?? 0,
            marks: q.marks,
            isAlternateEnable: q.isAlternateEnable ?? false,
          })
      );

      const subNoOfQuestions = subSectionQuestionTemplate.reduce(
        (sum, q) => sum + (q.noOfQuestion ?? 0),
        0
      );
      const subMarks = subSectionQuestionTemplate.reduce(
        (sum, q) => sum + (q.noOfQuestion ?? 0) * (q.marks ?? 0),
        0
      );
      noOfQuestions += subNoOfQuestions;
      marks += subMarks;

      return new SubSectionTemplate({
        subSectionTittle: sub.subSectionName ?? '',
        subSectionNumber: sub.sectionNumber,
        subSectionQuestionTemplate,
      });
    }
  );

  return new MultiSubjectSectionTemplate({
    sectionTittle: sectionTittle || undefined,
    noOfQuestions,
    marks,
    subSectionTemplate,
  });
};

// =============================================================

const sortMetaBySequence = (
  a: SectionQuestionMetaData,
  b: SectionQuestionMetaData
) => (a?.sequence ?? 0) - (b?.sequence ?? 0);

// =============================================================

/** Sorted child entries — groupedQuestions when isGrouped, else alternateQuestions. */
export const getChildQuestionsForMeta = (
  meta: SectionQuestionMetaData
): SectionQuestionMetaData[] => {
  const children = meta.isGrouped
    ? meta.groupedQuestions ?? []
    : meta.alternateQuestions ?? [];
  return [...children].sort(sortMetaBySequence);
};

// =============================================================

/** Main meta plus sorted children for display / reorder. */
export const getDisplayQuestionsForMeta = (
  meta: SectionQuestionMetaData
): SectionQuestionMetaData[] => [meta, ...getChildQuestionsForMeta(meta)];

// =============================================================

/** All question IDs for a meta row (main + children). */
export const getAllQuestionIdsFromMeta = (
  meta: SectionQuestionMetaData
): string[] =>
  getDisplayQuestionsForMeta(meta)
    .map((entry) => entry.questionId)
    .filter(Boolean);

// =============================================================

/** Flatten meta to a mutable list (nested arrays cleared) for replace/delete/reorder. */
export const flattenMetaForMutation = (
  meta: SectionQuestionMetaData
): SectionQuestionMetaData[] =>
  getDisplayQuestionsForMeta(meta).map(
    (val, index) =>
      new SectionQuestionMetaData({
        questionId: val.questionId,
        sequence: val.sequence,
        showMedia: val.showMedia,
        isAiGenerated: val.isAiGenerated,
        maxMarks: val.maxMarks ?? 0,
        questionType: val.questionType,
        alternateQuestions: [],
        groupedQuestions: [],
        // Row-level group fields live on the outer meta (index 0)
        ...(index === 0 && meta.isGrouped !== undefined
          ? { isGrouped: meta.isGrouped }
          : {}),
        ...(index === 0 && meta.groupQuestionHeading !== undefined
          ? { groupQuestionHeading: meta.groupQuestionHeading }
          : {}),
        ...(index === 0 && meta.groupQuestionAttemptedAny !== undefined
          ? { groupQuestionAttemptedAny: meta.groupQuestionAttemptedAny }
          : {}),
      })
  );

// =============================================================

/** Whether a meta row (main + children) contains the given question ID. */
export const metaContainsQuestionId = (
  meta: SectionQuestionMetaData,
  questionId: string
): boolean => getAllQuestionIdsFromMeta(meta).includes(questionId);

// =============================================================

/** Child/parent entry without row-level group fields (protobuf-es omits undefined). */
const metaWithoutGroupRowFields = (
  val: SectionQuestionMetaData,
  sequence: number
): SectionQuestionMetaData =>
  new SectionQuestionMetaData({
    questionId: val.questionId,
    sequence,
    showMedia: val.showMedia,
    isAiGenerated: val.isAiGenerated,
    maxMarks: val.maxMarks ?? 0,
    questionType: val.questionType,
    alternateQuestions: [],
    groupedQuestions: [],
  });

/** Build SectionQuestionMetaData after reordering display list. */
export const buildMetaFromDisplayList = (
  allQuestions: SectionQuestionMetaData[],
  outerSequence: number,
  isGrouped: boolean,
  /** Prefer original outer meta fields (survives deleting flat index 0). */
  groupRowFields?: {
    groupQuestionHeading?: string;
    groupQuestionAttemptedAny?: number;
  }
): SectionQuestionMetaData => {
  const newMain = allQuestions[0];
  if (isGrouped) {
    // Group-level fields must stay on the outer parent after reorder/promote.
    const groupQuestionHeading =
      groupRowFields?.groupQuestionHeading ??
      allQuestions.find((q) => q.groupQuestionHeading !== undefined)
        ?.groupQuestionHeading;
    const groupQuestionAttemptedAny =
      groupRowFields?.groupQuestionAttemptedAny ??
      allQuestions.find((q) => q.groupQuestionAttemptedAny !== undefined)
        ?.groupQuestionAttemptedAny;
    const children = allQuestions
      .slice(1)
      .map((val, i) => metaWithoutGroupRowFields(val, i + 1));
    return new SectionQuestionMetaData({
      ...metaWithoutGroupRowFields(newMain, outerSequence),
      isGrouped: true,
      ...(groupQuestionHeading !== undefined
        ? { groupQuestionHeading }
        : {}),
      ...(groupQuestionAttemptedAny !== undefined
        ? { groupQuestionAttemptedAny }
        : {}),
      groupedQuestions: children,
      alternateQuestions: [],
      sequence: outerSequence,
    });
  }
  const children = allQuestions
    .slice(1)
    .map((val, i) => new SectionQuestionMetaData({ ...val, sequence: i + 1 }));
  return new SectionQuestionMetaData({
    ...newMain,
    alternateQuestions: children,
    sequence: outerSequence,
  });
};

// =============================================================

/**
 * When subsection Attempt any is on and a row is grouped, `marks` is the group total
 * (passage "Marks per question").
 * - With group attempt-any: per child = marks / groupQuestionAttemptedAny
 * - Otherwise: per child = marks / noOfQuestion
 */
export const getEffectiveMarksForSubSectionQuestion = (props: {
  marks: number;
  noOfQuestion: number;
  isGrouped?: boolean;
  attemptAnyEnabled: boolean;
  groupQuestionAttemptedAny?: number;
}): { groupMarks: number; perChildMarks: number } => {
  const {
    marks,
    noOfQuestion,
    isGrouped,
    attemptAnyEnabled,
    groupQuestionAttemptedAny,
  } = props;
  const safeMarks = marks ?? 0;
  if (attemptAnyEnabled && isGrouped) {
    if (groupQuestionAttemptedAny !== undefined) {
      return {
        groupMarks: safeMarks,
        perChildMarks: safeMarks / Math.max(1, groupQuestionAttemptedAny),
      };
    }
    if (noOfQuestion > 1) {
      return {
        groupMarks: safeMarks,
        perChildMarks: safeMarks / noOfQuestion,
      };
    }
  }
  return { groupMarks: safeMarks, perChildMarks: safeMarks };
};

// =============================================================

/**
 * Temporary until BE accepts float marks on SubSectionQuestionType / QuestionTypeMarks.
 * To send the raw float (e.g. 1.333...): comment out Math.round and uncomment `return perChildMarks`.
 */
export const toRequestMarks = (perChildMarks: number): number => {
  // return Math.round(perChildMarks);
  return perChildMarks;
};

// =============================================================

/** Whole number when exact; otherwise up to two decimal places (e.g. 1, 1.5, 1.33). */
export const formatMarksDisplay = (marks: number): string => {
  const rounded = Math.round(marks * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : parseFloat(rounded.toFixed(2)).toString();
};

// =============================================================

/** Instruction shown above a subsection's first question when Attempt any is on. */
export const getSubSectionAttemptAnyInstruction = (count: number) =>
  `Attempt any ${count} question${count === 1 ? '' : 's'} from the following:`;

// =============================================================

/**
 * Creation-time marks total for a subsection (qpg_subsection_data).
 * Passage attempt any: attemptAnyCount × marks of the first defined question row.
 * Grouped row attempt any: groupQuestionAttemptedAny × marks.
 * Otherwise: sum of noOfQuestion × marks.
 */
export const getQpgCreationSubSectionMarksTotal = (subSection: {
  attemptAnyCount?: number;
  subSectionQuestions?: SubSectionQuestionType[];
}): number => {
  const questions = subSection.subSectionQuestions ?? [];
  if (subSection.attemptAnyCount !== undefined) {
    const firstDefinedRow = questions.find(
      (q) => q.questionType !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
    );
    const marksPerQuestion =
      firstDefinedRow?.marks && firstDefinedRow.marks > 0
        ? firstDefinedRow.marks
        : 1;
    return Math.max(1, subSection.attemptAnyCount) * marksPerQuestion;
  }
  return questions.reduce((sum, question) => {
    const marks = question.marks || 0;
    if (
      question.isGrouped &&
      question.groupQuestionAttemptedAny !== undefined
    ) {
      return sum + Math.max(1, question.groupQuestionAttemptedAny) * marks;
    }
    return sum + (question.noOfQuestion || 0) * marks;
  }, 0);
};

// =============================================================

/** Marks total for a meta row. Grouped rows sum parent + groupedQuestions maxMarks.
 * Group attempt-any uses groupQuestionAttemptedAny × per-question marks. */
const getTotalMaxMarksForMeta = (meta: SectionQuestionMetaData): number => {
  if (meta.isGrouped && meta.groupQuestionAttemptedAny !== undefined) {
    const perQuestionMarks = meta.maxMarks ?? 0;
    return Math.max(0, meta.groupQuestionAttemptedAny) * perQuestionMarks;
  }
  if (meta.isGrouped) {
    return getDisplayQuestionsForMeta(meta).reduce(
      (sum, entry) => sum + (entry.maxMarks ?? 0),
      0
    );
  }
  return meta.maxMarks ?? 0;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** Auto-generated grouped header body (ignores custom groupQuestionHeading). */
export const getDefaultGroupedQuestionHeaderBody = (props: {
  meta: SectionQuestionMetaData;
  questionType?: Question_QuestionEnum;
}): string => {
  const { meta, questionType } = props;
  const resolvedType = questionType ?? meta.questionType;
  const typeTitle = resolvedType
    ? getQuestionTypeTitleAndEnum(resolvedType)?.title ?? 'Unknown'
    : 'Unknown';
  const totalMarks = formatMarksDisplay(getTotalMaxMarksForMeta(meta));
  const attemptAnySuffix =
    meta.groupQuestionAttemptedAny !== undefined
      ? ` ( Attempt any: ${meta.groupQuestionAttemptedAny} )`
      : '';
  return `${typeTitle} (${totalMarks} Marks)${attemptAnySuffix}`;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** Body text for grouped header (no Q number), e.g. "MCQ Single (2 Marks)".
 * Prefers a custom groupQuestionHeading when set. */
export const getGroupedQuestionHeaderBody = (props: {
  meta: SectionQuestionMetaData;
  questionType?: Question_QuestionEnum;
}): string => {
  const { meta, questionType } = props;
  const customHeading = meta.groupQuestionHeading?.trim();
  if (customHeading) {
    return customHeading;
  }
  return getDefaultGroupedQuestionHeaderBody({ meta, questionType });
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** Header label for grouped questions, e.g. "Q1. MCQ Single (2 Marks)". */
export const getGroupedQuestionHeaderLabel = (props: {
  questionNumber: number;
  meta: SectionQuestionMetaData;
  questionType?: Question_QuestionEnum;
}): string => {
  const { questionNumber, meta, questionType } = props;
  return `Q${questionNumber}. ${getGroupedQuestionHeaderBody({
    meta,
    questionType,
  })}`;
};

// =============================================================

/** Ensure grouped meta rows have groupQuestionHeading set to the displayed default. */
export const withSeededGroupedQuestionHeading = (
  meta: SectionQuestionMetaData,
  questionType?: Question_QuestionEnum
): SectionQuestionMetaData => {
  if (!meta.isGrouped) return meta;
  if (meta.groupQuestionHeading?.trim()) return meta;
  return new SectionQuestionMetaData({
    ...meta,
    groupQuestionHeading: getDefaultGroupedQuestionHeaderBody({
      meta,
      questionType: questionType ?? meta.questionType,
    }),
  });
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** Seed missing groupQuestionHeading on all grouped metas in section list. */
export const seedGroupedQuestionHeadingsOnSections = (
  sections: MultiSubjectSection[]
): { sections: MultiSubjectSection[]; changed: boolean } => {
  let changed = false;
  const nextSections = sections.map((section) => {
    let sectionChanged = false;
    const nextSubs = (section.multiSubjectSubSections ?? []).map((sub) => {
      let subChanged = false;
      const nextMeta = (sub.subSectionMetaData ?? []).map((meta) => {
        if (!meta.isGrouped || meta.groupQuestionHeading?.trim()) {
          return meta;
        }
        subChanged = true;
        changed = true;
        return withSeededGroupedQuestionHeading(meta);
      });
      if (!subChanged) return sub;
      sectionChanged = true;
      return new MultiSubjectSubSection({
        ...sub,
        subSectionMetaData: nextMeta,
      });
    });
    if (!sectionChanged) return section;
    return new MultiSubjectSection({
      ...section,
      multiSubjectSubSections: nextSubs,
    });
  });
  return { sections: nextSections, changed };
};

// =============================================================

/** Convert a positive integer to a lowercase Roman numeral (e.g. 1 → "i", 4 → "iv"). */
export const toRomanNumeral = (num: number): string => {
  if (!Number.isInteger(num) || num < 1 || num > 3999) {
    return String(num);
  }
  const romanNumeralMap: { value: number; numeral: string }[] = [
    { value: 1000, numeral: 'm' },
    { value: 900, numeral: 'cm' },
    { value: 500, numeral: 'd' },
    { value: 400, numeral: 'cd' },
    { value: 100, numeral: 'c' },
    { value: 90, numeral: 'xc' },
    { value: 50, numeral: 'l' },
    { value: 40, numeral: 'xl' },
    { value: 10, numeral: 'x' },
    { value: 9, numeral: 'ix' },
    { value: 5, numeral: 'v' },
    { value: 4, numeral: 'iv' },
    { value: 1, numeral: 'i' },
  ];
  let remaining = num;
  let roman = '';
  for (const { value, numeral } of romanNumeralMap) {
    while (remaining >= value) {
      roman += numeral;
      remaining -= value;
    }
  }
  return roman;
};

// =============================================================

/**
 * Returns unique question IDs from all sections of qpg_content
 * (main questions, alternate questions, and grouped questions in every subSectionMetaData).
 */
export const getUniqueQuestionIdsFromQpgContent = (props: {
  qpgContent: AssessmentContent | undefined;
  sectionNumbersToIgnore?: number[];
}): string[] => {
  const { qpgContent, sectionNumbersToIgnore } = props;
  if (!qpgContent?.multiSubjectSections?.length) return [];

  const ignoreSet = sectionNumbersToIgnore?.length
    ? new Set(sectionNumbersToIgnore)
    : undefined;

  const ids = new Set<string>();
  for (const section of qpgContent.multiSubjectSections) {
    if (ignoreSet?.has(section.sectionNo ?? 0)) continue;

    for (const subSection of section.multiSubjectSubSections ?? []) {
      for (const meta of subSection.subSectionMetaData ?? []) {
        for (const id of getAllQuestionIdsFromMeta(meta)) {
          ids.add(id);
        }
      }
    }
  }
  return Array.from(ids);
};

// =============================================================

/** Question IDs that contribute to marks totals (grouped children included; alternates excluded). */
const getQuestionIdsForMarksTotal = (meta: SectionQuestionMetaData): string[] =>
  meta.isGrouped
    ? getAllQuestionIdsFromMeta(meta)
    : meta.questionId
    ? [meta.questionId]
    : [];

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * Marks for one attemptable unit under Attempt-any.
 * Grouped rows use the full group total (not a single child's maxMarks) — creation UI
 * stores group total on the row, while questionTypeAndMarks / meta children store per-child.
 * Prefer subsection meta / questionTypeAndMarks over paper-level getMarksForQuestionId.
 */
export const getAttemptAnyMarksPerQuestion = (props: {
  subSection: {
    subSectionMetaData?: SectionQuestionMetaData[];
    questionTypeAndMarks?: { marks?: number }[];
  };
  getMarksForQuestionId?: (questionId: string) => number;
}): number => {
  const { subSection, getMarksForQuestionId } = props;
  const meta = subSection.subSectionMetaData ?? [];
  if (!meta[0]) {
    return subSection.questionTypeAndMarks?.[0]?.marks ?? 0;
  }
  if (meta[0].isGrouped) {
    const fromMeta = getTotalMaxMarksForMeta(meta[0]);
    if (fromMeta) return fromMeta;
    if (getMarksForQuestionId) {
      return getQuestionIdsForMarksTotal(meta[0]).reduce(
        (sum, id) => sum + (getMarksForQuestionId(id) ?? 0),
        0
      );
    }
    return 0;
  }
  if (typeof meta[0].maxMarks === 'number' && meta[0].maxMarks > 0) {
    return meta[0].maxMarks;
  }
  const typeMarks = subSection.questionTypeAndMarks?.[0]?.marks;
  if (typeof typeMarks === 'number' && typeMarks > 0) {
    return typeMarks;
  }
  if (getMarksForQuestionId) {
    const firstId = getQuestionIdsForMarksTotal(meta[0])[0];
    if (firstId) {
      const marks = getMarksForQuestionId(firstId);
      if (marks) return marks;
    }
  }
  return 0;
};

/**
 * Returns total marks of a section by summing maxMarks from all subSectionMetaData.
 * Grouped rows include child maxMarks via getTotalMaxMarksForMeta.
 * Passage attempt-any subsections use attemptAnyCount * marks per question.
 * Grouped row attempt-any uses groupQuestionAttemptedAny × per-question marks.
 */
export const getSubSectionMarksTotal = (props: {
  subSection: {
    attemptAnyCount?: number;
    subSectionMetaData?: SectionQuestionMetaData[];
    questionTypeAndMarks?: { marks?: number }[];
  };
  getMarksForQuestionId?: (questionId: string) => number;
}): number => {
  const { subSection, getMarksForQuestionId } = props;
  const meta = subSection.subSectionMetaData ?? [];

  if (subSection.attemptAnyCount !== undefined) {
    if (!meta.length && !(subSection.questionTypeAndMarks?.length ?? 0)) {
      return 0;
    }
    return (
      Math.max(0, subSection.attemptAnyCount) *
      getAttemptAnyMarksPerQuestion({ subSection, getMarksForQuestionId })
    );
  }

  if (!meta.length) return 0;

  if (getMarksForQuestionId) {
    return meta.reduce((sum, entry) => {
      if (entry.isGrouped && entry.groupQuestionAttemptedAny !== undefined) {
        const ids = getQuestionIdsForMarksTotal(entry);
        const perMarks =
          (ids[0] ? getMarksForQuestionId(ids[0]) : undefined) ??
          entry.maxMarks ??
          0;
        return sum + Math.max(0, entry.groupQuestionAttemptedAny) * perMarks;
      }
      return (
        sum +
        getQuestionIdsForMarksTotal(entry).reduce(
          (idSum, id) => idSum + (getMarksForQuestionId(id) ?? 0),
          0
        )
      );
    }, 0);
  }

  return meta.reduce(
    (subTotal, entry) => subTotal + getTotalMaxMarksForMeta(entry),
    0
  );
};

// =============================================================

/**
 * Returns total marks of a section by summing subsection totals
 * (respects attempt-any equal marks).
 */
export const getTotalMarksOfSection = (
  section: MultiSubjectSection,
  getMarksForQuestionId?: (questionId: string) => number
): number => {
  return (section.multiSubjectSubSections ?? []).reduce(
    (sectionTotal, subSection) =>
      sectionTotal +
      getSubSectionMarksTotal({ subSection, getMarksForQuestionId }),
    0
  );
};

// =============================================================

export const getSubjectImageBySubjectId = (
  subjectId?: number,
  teachClassSubjects?: TeachClassSubjects[]
): string => {
  if (subjectId == null || !teachClassSubjects?.length) return '';
  for (const classData of teachClassSubjects) {
    for (const subjectData of classData.subjects ?? []) {
      if (subjectData.subjectId === subjectId && subjectData.iconUrl) {
        return getMediaBasePath(
          subjectData.iconUrl,
          'schoolnetStaticAssetBucket'
        );
      }
    }
  }
  return '';
};

// =============================================================

export const getSubjectColorBySubjectId = (
  subjectId?: number,
  teachClassSubjects?: TeachClassSubjects[]
): string => {
  if (subjectId == null || !teachClassSubjects?.length) return '';
  for (const classData of teachClassSubjects) {
    for (const subjectData of classData.subjects ?? []) {
      if (subjectData.subjectId === subjectId && subjectData.iconUrl) {
        return `${subjectData.textColor || '#000000'}33`;
      }
    }
  }
  return '';
};

// =============================================================

export const convertToMultiSubjectFormateInfo = (
  dataToConvert: {
    subject: { name: string; id: number } | null;
    marks: number | undefined;
  }[],
  teachClassSubjects?: TeachClassSubjects[]
): MultiSubjectFormateInfo[] => {
  return dataToConvert
    .filter((data) => data.subject?.id != null && (data.marks ?? 0) > 0)
    .map((data) => {
      const subjectId = data.subject?.id;
      const subject =
        getTeacherSubjectEnum(subjectId, teachClassSubjects) ??
        Subject_SubjectEnum.UNDEFINED;
      return new MultiSubjectFormateInfo({
        subjectId,
        subject,
        preferredMarks: data.marks,
      });
    });
};

// =============================================================

export const buildTaskInfoModels = (
  multiSubjectSectionMetaInfo: MultiSubjectSection[]
): TaskInfoModel[] => {
  let seq = 1;
  const tasks: TaskInfoModel[] = [];
  for (const section of multiSubjectSectionMetaInfo ?? []) {
    for (const subSection of section.multiSubjectSubSections ?? []) {
      for (const q of subSection.subSectionMetaData ?? []) {
        if (!q?.questionId) continue;
        tasks.push(
          new TaskInfoModel({
            questionId: q.questionId,
            sequence: seq,
            maxMarks: q.maxMarks !== 0 ? q.maxMarks : undefined,
            sectionNo: section.sectionNo,
          })
        );
        for (const child of getChildQuestionsForMeta(q)) {
          if (!child.questionId) continue;
          tasks.push(
            new TaskInfoModel({
              questionId: child.questionId,
              sequence: seq,
              maxMarks: child.maxMarks !== 0 ? child.maxMarks : undefined,
              sectionNo: section.sectionNo,
            })
          );
        }
        seq++;
      }
    }
  }
  return tasks;
};

// =============================================================

export const useDeleteQPGQuestion = () => {
  const {
    qpg_assessment_content,
    qpg_questions_sequence_info,
    qpg_question_module_map,
  } = deserify(useAppSelector((state) => state.assessment));
  const dispatch = useAppDispatch();
  const qpgAssessmentQuestions =
    qpg_assessment_content?.map((ques) => new Question(ques)) || [];
  const qpgQuestionsSequenceInfo = new TaskInfo(qpg_questions_sequence_info);
  const sectionMeta = qpgQuestionsSequenceInfo.questionSectionMetaInfo.sort(
    (a, b) => a.sectionNo - b.sectionNo
  ) as QuestionSectionMeta[];

  return (sectionIndex: number, questionIndex?: number, altIdx?: number) => {
    const section = sectionMeta[sectionIndex] as QuestionSectionMeta;
    if (!section) return;
    if (typeof questionIndex !== 'number') {
      return;
    }
    section.sectionQuestionsInfo = section.sectionQuestionsInfo.sort(
      (a, b) => a.sequence - b.sequence
    );
    const quesMeta = section.sectionQuestionsInfo[
      questionIndex
    ] as SectionQuestionMetaData;
    if (!quesMeta) return;
    const outerSequence = quesMeta.sequence;
    quesMeta.alternateQuestions = quesMeta.alternateQuestions.sort(
      (a, b) => a.sequence - b.sequence
    );
    const allQuestions = [
      quesMeta,
      ...quesMeta.alternateQuestions.sort((a, b) => a.sequence - b.sequence),
    ].map((val) => {
      val.alternateQuestions = [];
      return val;
    });
    let removed: SectionQuestionMetaData[] = [];
    if (typeof altIdx === 'number') {
      removed = allQuestions.splice(altIdx, 1);
    } else {
      // If altIdx is not provided, delete all
      removed = allQuestions.splice(0, allQuestions.length);
    }
    if (allQuestions.length > 0) {
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        ...allQuestions[0],
        alternateQuestions: allQuestions.slice(1).map((val, index) => ({
          ...val,
          sequence: index + 1,
        })),
        sequence: outerSequence,
      });
      section.sectionQuestionsInfo[questionIndex] = newSectionQuestionMetaData;
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
    } else {
      section.sectionQuestionsInfo.splice(questionIndex, 1);
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
    }
    const filteredQuestionIds = qpgQuestionsSequenceInfo.questionIds.filter(
      (id) => !removed.map((val) => val.questionId).includes(id)
    );
    qpgQuestionsSequenceInfo.questionIds = filteredQuestionIds;
    const filteredQuestionModuleMap = qpg_question_module_map?.filter(
      (val) =>
        !removed.map((val) => val.questionId).includes(val.questionId || '')
    );
    if (sectionMeta[sectionIndex].sectionQuestionsInfo.length === 0) {
      qpgQuestionsSequenceInfo.questionSectionMetaInfo.splice(sectionIndex, 1);
    }
    dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
    const filteredQpgAssessmentQuestions = qpgAssessmentQuestions.filter(
      (ques) =>
        !removed.map((val) => val.questionId).includes(ques.questionId || '')
    );
    dispatch(setQPGAssessmentContent(filteredQpgAssessmentQuestions));
    dispatch(setQPGQuestionModuleMap(filteredQuestionModuleMap || []));
  };
};

// =============================================================

/**
 * Merges two chapterTopicsInfo arrays by chapterId, then union of topics by topicId.
 * Preserves first-seen chapter/topic metadata (name, chapterNo, etc.).
 */

export const mergeChapterTopicsInfoUnion = (
  a: ChapterIdsInfo[],
  b: ChapterIdsInfo[]
): ChapterIdsInfo[] => {
  const byChapterId = new Map<
    number,
    { chapter: ChapterIdsInfo; topics: Map<number, TopicIdsInfo> }
  >();
  for (const c of [...a, ...b]) {
    const id = c.chapterId;
    if (!byChapterId.has(id)) {
      byChapterId.set(id, {
        chapter: c,
        topics: new Map(c.topicInfo.map((t) => [t.topicId, t])),
      });
    } else {
      const entry = byChapterId.get(id)!;
      for (const t of c.topicInfo) {
        entry.topics.set(t.topicId, t);
      }
    }
  }
  return Array.from(byChapterId.values()).map(
    ({ chapter, topics }) =>
      new ChapterIdsInfo({
        chapterId: chapter.chapterId,
        name: chapter.name,
        chapterNo: chapter.chapterNo,
        noOfQuestions: chapter.noOfQuestions,
        subjectId: chapter.subjectId,
        topicInfo: Array.from(topics.values()),
      })
  );
};

// =============================================================

const QUESTION_TYPE_TO_CONFIG_KEYS: ReadonlyArray<Question_QuestionEnum> = [
  Question_QuestionEnum.QUESTION_TYPE_MCQS,
  Question_QuestionEnum.QUESTION_TYPE_MCQM,
  Question_QuestionEnum.QUESTION_TYPE_FIB,
  Question_QuestionEnum.QUESTION_TYPE_TF,
  Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_LONG,
  Question_QuestionEnum.QUESTION_TYPE_CBQ,
];

export const getAvailableCountForQuestionType = (props: {
  questionType: Question_QuestionEnum;
  noOfQuestionsByTypes?: QuestionTypeCount;
}): number => {
  const { questionType, noOfQuestionsByTypes } = props;
  if (!noOfQuestionsByTypes) return Number.MAX_SAFE_INTEGER;
  const map: Partial<Record<Question_QuestionEnum, number>> = {
    [Question_QuestionEnum.QUESTION_TYPE_MCQS]: noOfQuestionsByTypes.noOfMcqs,
    [Question_QuestionEnum.QUESTION_TYPE_MCQM]: noOfQuestionsByTypes.noOfMcqm,
    [Question_QuestionEnum.QUESTION_TYPE_FIB]: noOfQuestionsByTypes.noOfFib,
    [Question_QuestionEnum.QUESTION_TYPE_TF]: noOfQuestionsByTypes.noOfTf,
    [Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT]:
      noOfQuestionsByTypes.noOfVeryShort,
    [Question_QuestionEnum.QUESTION_TYPE_SHORT]: noOfQuestionsByTypes.noOfShort,
    [Question_QuestionEnum.QUESTION_TYPE_LONG]: noOfQuestionsByTypes.noOfLong,
    [Question_QuestionEnum.QUESTION_TYPE_CBQ]: noOfQuestionsByTypes.noOfCbq,
  };
  const available = map[questionType];
  return typeof available === 'number' ? available : Number.MAX_SAFE_INTEGER;
};

// =============================================================

/**
 * Returns a mutable map of question type -> available count from config.
 * Used when building subsection data from template so we can allocate subsection-by-subsection (first subsection gets priority).
 */
export const getInitialRemainingByType = (
  noOfQuestionsByTypes: QuestionTypeCount | undefined
): Map<Question_QuestionEnum, number> => {
  const map = new Map<Question_QuestionEnum, number>();
  QUESTION_TYPE_TO_CONFIG_KEYS.forEach((questionType) => {
    map.set(
      questionType,
      getAvailableCountForQuestionType({
        questionType,
        noOfQuestionsByTypes,
      })
    );
  });
  return map;
};

// =============================================================

/**
 * Sum of noOfQuestion per question type across subsections.
 * If excludeSubSectionIndex is provided, that subsection is excluded (for "used in other subsections").
 */
const getUsedCountByQuestionType = (
  subsectionData: SubSectionType[],
  excludeSubSectionIndex?: number
): Map<Question_QuestionEnum, number> => {
  const used = new Map<Question_QuestionEnum, number>();
  (subsectionData ?? []).forEach((sub, subIdx) => {
    if (
      excludeSubSectionIndex !== undefined &&
      subIdx === excludeSubSectionIndex
    )
      return;
    (sub.subSectionQuestions ?? []).forEach((q) => {
      const type =
        q.questionType ?? Question_QuestionEnum.QUESTION_TYPE_UNDEFINED;
      if (type === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) return;
      used.set(type, (used.get(type) ?? 0) + (q.noOfQuestion ?? 0));
    });
  });
  return used;
};

// =============================================================

/**
 * Remaining count per question type for a given subsection:
 * config count minus usage in all other subsections (excluding subSectionIndex).
 */
export const getRemainingForSubSection = (
  subsectionData: SubSectionType[],
  subSectionIndex: number,
  noOfQuestionsByTypes: QuestionTypeCount | undefined
): Map<Question_QuestionEnum, number> => {
  const remaining = new Map<Question_QuestionEnum, number>();
  if (!noOfQuestionsByTypes) {
    QUESTION_TYPE_TO_CONFIG_KEYS.forEach((t) =>
      remaining.set(t, Number.MAX_SAFE_INTEGER)
    );
    return remaining;
  }
  const usedInOthers = getUsedCountByQuestionType(
    subsectionData ?? [],
    subSectionIndex
  );
  QUESTION_TYPE_TO_CONFIG_KEYS.forEach((questionType) => {
    const total = getAvailableCountForQuestionType({
      questionType,
      noOfQuestionsByTypes,
    });
    const used = usedInOthers.get(questionType) ?? 0;
    remaining.set(questionType, Math.max(0, total - used));
  });
  return remaining;
};

// =============================================================

/** Question types allowed in AI mode (each subsection can have up to maxPerType per type). */
const AI_QUESTION_TYPES: ReadonlyArray<Question_QuestionEnum> = [
  Question_QuestionEnum.QUESTION_TYPE_MCQS,
  Question_QuestionEnum.QUESTION_TYPE_MCQM,
  Question_QuestionEnum.QUESTION_TYPE_FIB,
  Question_QuestionEnum.QUESTION_TYPE_TF,
  Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_SHORT,
  Question_QuestionEnum.QUESTION_TYPE_LONG,
  Question_QuestionEnum.QUESTION_TYPE_CBQ,
];

/**
 * When allow_ai_questions is true: remaining per question type for a subsection
 * is (maxPerType - used in that subsection). Each subsection gets its own cap.
 */
export const getRemainingForSubSectionAIMode = (
  subsectionData: SubSectionType[],
  subSectionIndex: number,
  maxPerType = 25
): Map<Question_QuestionEnum, number> => {
  const remaining = new Map<Question_QuestionEnum, number>();
  const sub = subsectionData?.[subSectionIndex];
  const usedInThisSubsection = new Map<Question_QuestionEnum, number>();
  (sub?.subSectionQuestions ?? []).forEach((q) => {
    const type =
      q.questionType ?? Question_QuestionEnum.QUESTION_TYPE_UNDEFINED;
    if (type === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) return;
    usedInThisSubsection.set(
      type,
      (usedInThisSubsection.get(type) ?? 0) + (q.noOfQuestion ?? 0)
    );
  });
  AI_QUESTION_TYPES.forEach((questionType) => {
    const used = usedInThisSubsection.get(questionType) ?? 0;
    remaining.set(questionType, Math.max(0, maxPerType - used));
  });
  return remaining;
};

// =============================================================

/**
 * True if at least one question type has remaining capacity (total from config minus used across all subsections > 0).
 * Used to enable/disable "ADD SUB SECTION" when all types are exhausted.
 */
export const hasAnyQuestionTypeRemaining = (
  subsectionData: SubSectionType[],
  noOfQuestionsByTypes: QuestionTypeCount | undefined
): boolean => {
  if (!noOfQuestionsByTypes) return true;
  const used = getUsedCountByQuestionType(subsectionData ?? []);
  return QUESTION_TYPE_TO_CONFIG_KEYS.some((questionType) => {
    const total = getAvailableCountForQuestionType({
      questionType,
      noOfQuestionsByTypes,
    });
    if (total === Number.MAX_SAFE_INTEGER) return true;
    const usedCount = used.get(questionType) ?? 0;
    return usedCount < total;
  });
};

// =============================================================

export function areAllSubSectionQuestionsFilled(
  questions: SubSectionQuestionType[] | undefined
): boolean {
  if (!questions?.length) return true;
  return questions.every(
    (q) =>
      q.questionType !== undefined &&
      q.questionType !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED &&
      (q.marks ?? 0) > 0 &&
      (q.noOfQuestion ?? 0) > 0
  );
}

// =============================================================

/** True when subsection is a passage / comprehension block (creation or qpg_content). */
export const isPassageSectionEnabled = (sub?: {
  subSectionType?: QpgSubsectionTypeEnum;
}) => sub?.subSectionType === QpgSubsectionTypeEnum.QPG_SUBSECTION_TYPE_PASSAGE;

// =============================================================

export const MIN_PASSAGE_WORD_COUNT = 50;

const PASSAGE_INSTRUCTION_Q_RANGE_RE = /\(\s*Q\d+\s*-\s*Q\d+\s*\)/;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const formatPassageInstructionQRange = (start: number, end: number): string =>
  `( Q${start} - Q${end} )`;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const getDefaultPassageInstruction = (props: {
  start: number;
  end: number;
}): string =>
  `Read the passage to answer questions. ${formatPassageInstructionQRange(
    props.start,
    props.end
  )}`;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const replacePassageInstructionQRange = (props: {
  instruction: string;
  start: number;
  end: number;
}): string => {
  const { instruction, start, end } = props;
  if (!PASSAGE_INSTRUCTION_Q_RANGE_RE.test(instruction)) {
    return instruction;
  }
  return instruction.replace(
    PASSAGE_INSTRUCTION_Q_RANGE_RE,
    formatPassageInstructionQRange(start, end)
  );
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const splitPassageTextWithInstruction = (
  stored: string | undefined
): { instruction: string; body: string } => {
  const text = stored ?? '';
  const separatorIndex = text.indexOf('\n\n');
  if (separatorIndex === -1) {
    // Legacy instruction-only (no trailing separator): detect via Q-range token.
    if (PASSAGE_INSTRUCTION_Q_RANGE_RE.test(text)) {
      return { instruction: text, body: '' };
    }
    return { instruction: '', body: text };
  }
  // Composed format is always instruction\n\nbody (instruction may be empty).
  return {
    instruction: text.slice(0, separatorIndex),
    body: text.slice(separatorIndex + 2),
  };
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const composePassageTextWithInstruction = (props: {
  instruction: string;
  body: string;
}): string => {
  const { instruction, body } = props;
  // Always keep the separator so an intentionally cleared instruction ("\n\nbody")
  // is distinct from legacy body-only text and from a brand-new empty string.
  return `${instruction.trimEnd()}\n\n${body}`;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** True when stored text uses instruction\n\nbody (including empty instruction). */
export const hasPassageInstructionSeparator = (
  stored: string | undefined
): boolean => (stored ?? '').includes('\n\n');

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const countWords = (text: string | undefined): number => {
  const trimmed = text?.trim() ?? '';
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/** Manual passage editor must have at least {@link MIN_PASSAGE_WORD_COUNT} words (body only). */
export const isPassageTextMinLengthMet = (text: string | undefined): boolean =>
  countWords(splitPassageTextWithInstruction(text).body) >=
  MIN_PASSAGE_WORD_COUNT;

// =============================================================

/** All chapter + topic module ids from Step 1 selection (default passage source). */
export const getAllPassageModuleIdsFromChapters = (
  chapters: { chapterId: number; topics?: { topicId: number }[] }[] | undefined
): number[] => {
  if (!chapters?.length) return [];
  return chapters.flatMap((chapter) => [
    chapter.chapterId,
    ...(chapter.topics ?? []).map((topic) => topic.topicId),
  ]);
};

/**
 * When Step 1 chapters/topics change, reseed every passage subsection's
 * `passageModuleIds` to the full current chapter+topic set.
 */
export const resetPassageModuleIdsInSubsections = (
  subsections: SubSectionTypeV2[] | undefined,
  moduleIds: number[]
): SubSectionTypeV2[] | undefined => {
  if (!subsections?.length) return subsections;
  let changed = false;
  const next = subsections.map((sub) => {
    if (!isPassageSectionEnabled(sub)) return sub;
    const current = sub.passageModuleIds ?? [];
    const same =
      current.length === moduleIds.length &&
      current.every((id, i) => id === moduleIds[i]);
    if (same) return sub;
    changed = true;
    return new SubSectionTypeV2({
      ...sub,
      passageModuleIds: [...moduleIds],
    });
  });
  return changed ? next : subsections;
};

// =============================================================
